import React, { useEffect, useMemo } from 'react';
import { Grid, Card, CardContent, Typography, Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { fetchJobs } from '@/actions/jobs';
import { fetchDatasets } from '@/actions/datasets';
import { fetchModels } from '@/actions/models';
import { fetchCheckpoints } from '@/actions/checkpoints';
import STATUSES from './statuses';
import JOB_TYPES from './jobs/types';

function Dashboard() {
  const dispatch = useDispatch();
  const user = useUser();
  const jobs = useSelector((state) => state.jobs || []);
  const datasets = useSelector((state) => state.datasets || []);
  const models = useSelector((state) => state.models || []);
  const checkpoints = useSelector((state) => state.checkpoints || []);

  useEffect(() => {
    if (user?.selected_project) {
      dispatch(fetchJobs(user.selected_project));
      dispatch(fetchDatasets(user.selected_project));
      dispatch(fetchModels(user.selected_project));
      dispatch(fetchCheckpoints(user.selected_project));
    }
  }, [dispatch, user?.selected_project]);

  const counts = useMemo(() => {
    const notebookCount = jobs.filter(
      (job) => job.type === JOB_TYPES.NOTEBOOK.value,
    ).length;
    const notebookRunningCount = jobs.filter(
      (job) =>
        job.type === JOB_TYPES.NOTEBOOK.value &&
        job.status === STATUSES.RUNNING,
    ).length;
    const trainingCount = jobs.filter(
      (job) => job.type === JOB_TYPES.TRAINING.value,
    ).length;
    const trainingRunningCount = jobs.filter(
      (job) =>
        job.type === JOB_TYPES.TRAINING.value &&
        job.status === STATUSES.RUNNING,
    ).length;
    const inferenceCount = jobs.filter(
      (job) => job.type === JOB_TYPES.INFERENCE.value,
    ).length;
    const inferenceRunningCount = jobs.filter(
      (job) =>
        job.type === JOB_TYPES.INFERENCE.value &&
        job.status === STATUSES.RUNNING,
    ).length;
    const endpointCount = jobs.filter(
      (job) => job.type === JOB_TYPES.ENDPOINT.value,
    ).length;
    const endpointRunningCount = jobs.filter(
      (job) =>
        job.type === JOB_TYPES.ENDPOINT.value &&
        job.status === STATUSES.RUNNING,
    ).length;
    const llmCount = jobs.filter(
      (job) => job.type === JOB_TYPES.LLM.value,
    ).length;
    const llmRunningCount = jobs.filter(
      (job) =>
        job.type === JOB_TYPES.LLM.value &&
        job.status === STATUSES.RUNNING,
    ).length;
    const guardrailCount = jobs.filter(
      (job) => job.type === JOB_TYPES.GUARDRAIL.value,
    ).length;
    const guardrailRunningCount = jobs.filter(
      (job) =>
        job.type === JOB_TYPES.GUARDRAIL.value &&
        job.status === STATUSES.RUNNING,
    ).length;
    const datasetCount = datasets.filter(
      (dataset) => dataset.status === STATUSES.READY,
    ).length;
    const modelCount = models.filter(
      (model) => model.status === STATUSES.READY,
    ).length;
    const checkpointCount = checkpoints.filter(
      (checkpoint) => checkpoint.status === STATUSES.READY,
    ).length;

    return {
      notebookCount,
      notebookRunningCount,
      trainingCount,
      trainingRunningCount,
      inferenceCount,
      inferenceRunningCount,
      endpointCount,
      endpointRunningCount,
      llmCount,
      llmRunningCount,
      guardrailCount,
      guardrailRunningCount,
      datasetCount,
      modelCount,
      checkpointCount,
    };
  }, [jobs, datasets, models, checkpoints]);

  return (
    <Box sx={{ mt: 2.5, px: 2 }}>
      <Grid container spacing={3} justifyContent='center'>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Resources
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/jobs/notebook'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Notebooks</Typography>
                    <Typography>Total Count: {counts.notebookCount}</Typography>
                    <Typography>
                      Currently Running: {counts.notebookRunningCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/jobs/training'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Training Jobs</Typography>
                    <Typography>Total Count: {counts.trainingCount}</Typography>
                    <Typography>
                      Currently Running: {counts.trainingRunningCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/jobs/inference'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Inference Jobs</Typography>
                    <Typography>
                      Total Count: {counts.inferenceCount}
                    </Typography>
                    <Typography>
                      Currently Running: {counts.inferenceRunningCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/jobs/endpoint'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Endpoints</Typography>
                    <Typography>Total Count: {counts.endpointCount}</Typography>
                    <Typography>
                      Currently Running: {counts.endpointRunningCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/jobs/llm'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>LLMs</Typography>
                    <Typography>Total Count: {counts.llmCount}</Typography>
                    <Typography>
                      Currently Running: {counts.llmRunningCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/jobs/guardrail'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Guardrails</Typography>
                    <Typography>
                      Total Count: {counts.guardrailCount}
                    </Typography>
                    <Typography>
                      Currently Running: {counts.guardrailRunningCount}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/dataset'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Datasets</Typography>
                    <Typography>Total Count: {counts.datasetCount}</Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/model'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Models</Typography>
                    <Typography>Total Count: {counts.modelCount}</Typography>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Box
                    component={RouterLink}
                    to='/checkpoint'
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                    }}
                  >
                    <Typography variant='h6'>Checkpoints</Typography>
                    <Typography>
                      Total Count: {counts.checkpointCount}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Getting Started
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Link component={RouterLink} to='/account/billing'>
                    <Typography>Add Credits</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link component={RouterLink} to='/jobs/notebook/new'>
                    <Typography>Start a Notebook</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link component={RouterLink} to='/jobs/training/new'>
                    <Typography>Create a Training Job</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link component={RouterLink} to='/jobs/inference/new'>
                    <Typography>Run an Inference Job</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link component={RouterLink} to='/jobs/endpoint/new'>
                    <Typography>Deploy an Inference Endpoint</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link component={RouterLink} to='/jobs/llm/new'>
                    <Typography>Deploy an LLM</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link component={RouterLink} to='/jobs/guardrail/new'>
                    <Typography>Deploy a Guardrail</Typography>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Documentation
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/getting-started/creating-account/`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>Getting Started Guide</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/tutorials/basic/jupyter-notebook-pytorch-imagenet/`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>Notebooks Tutorial</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/tutorials/basic/parallel-experiments-with-notebook-copying/`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>
                      Parallel Training Experiments with Notebooks
                    </Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/tutorials/basic/tensorflow-cifar10-image-classification/`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>
                      Simple Image Classifier Training Job
                    </Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/tutorials/ml-pipeline/pytorch-pneumonia-detection-pipeline/`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>
                      Full Training and Inference Pipeline
                    </Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/tutorials/hyperparameter-tuning/hyperopt-mxnet-voc-object-detection/`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>Parallel Hyperparameter Tuning</Typography>
                  </Link>
                </Grid>
                <Grid size={12}>
                  <Link
                    href={`${import.meta.env.VITE_DOC_URL}/tutorials/gan/stable-diffusion-2-custom-model-training`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Typography>
                      Stable Diffusion 2 Dreambooth Training
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
